import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { loadTokenConfig } from '../../lib/redux/sheets/slice';

interface ApiCallOptions {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: any;
  maxRetries?: number;
  tokenConfig?: any; // Optional override for token config
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  status?: number;
}


export function useSpreadsheetApiCall() {
  const dispatch = useDispatch();
  const tokenConfig = useSelector((state: RootState) => state.googleSheets.auth.tokenConfig);
  
  const makeApiCall = useCallback(async <T = any>(options: ApiCallOptions): Promise<ApiResponse<T>> => {
    const { url, method = 'GET', body, maxRetries = 3, tokenConfig: providedTokenConfig } = options;
    const callId = `${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    const startTime = Date.now();


    // Use provided token config or fall back to hook's token config
    const activeTokenConfig = providedTokenConfig || tokenConfig;

    if (!activeTokenConfig) {
      return { success: false, error: 'No token config available' };
    }

    let retryCount = 0;
    let backoffDelay = 1000; // Start with 1 second

    while (retryCount <= maxRetries) {
      const attemptNumber = retryCount + 1;

      try {
        const headers: Record<string, string> = {
          'Authorization': `Bearer ${activeTokenConfig.access_token}`,
        };

        if (body) {
          headers['Content-Type'] = 'application/json';
        }

        const fetchStart = Date.now();
        const response = await fetch(url, {
          method,
          headers,
          body: body ? JSON.stringify(body) : undefined,
        });
        const fetchTime = Date.now() - fetchStart;



        // Handle rate limiting (429)
        if (response.status === 429) {
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, backoffDelay));
            backoffDelay *= 2; // Exponential backoff
            retryCount++;
            continue;
          } else {
            const totalTime = Date.now() - startTime;
            return {
              success: false,
              error: 'Rate limit exceeded after max retries',
              status: response.status
            };
          }
        }

        // Handle token refresh needed (401, 403, or specific 404 cases)
        if (response.status === 401 || response.status === 403) {
          if (retryCount < maxRetries) {
            try {
              // Attempt to refresh token via backend with force_refresh = true
              await loadTokenConfig(dispatch, true);

              // Wait a bit for the token to be refreshed
              await new Promise(resolve => setTimeout(resolve, 500));

              retryCount++;
              continue;
            } catch (refreshError) {
              return {
                success: false,
                error: `Token refresh failed: ${refreshError}`,
                status: response.status
              };
            }
          } else {
            const totalTime = Date.now() - startTime;
            return {
              success: false,
              error: 'Authentication failed after token refresh attempts',
              status: response.status
            };
          }
        }

        // Handle other client errors (4xx) - don't retry these
        if (response.status >= 400 && response.status < 500 && response.status !== 429) {
          const errorText = await response.text().catch(() => 'Unknown error');
          const totalTime = Date.now() - startTime;
          return {
            success: false,
            error: `Client error: ${errorText}`,
            status: response.status
          };
        }

        // Handle server errors (5xx) - retry these
        if (response.status >= 500) {
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, backoffDelay));
            backoffDelay *= 2; // Exponential backoff
            retryCount++;
            continue;
          } else {
            const totalTime = Date.now() - startTime;
            return {
              success: false,
              error: `Server error after max retries`,
              status: response.status
            };
          }
        }

        // Success case
        if (response.ok) {
          try {
            const data = await response.json();
            const totalTime = Date.now() - startTime;
            const dataSize = JSON.stringify(data).length;

            return { success: true, data, status: response.status };
          } catch (jsonError) {
            // Response might not be JSON (e.g., for some PUT requests)
            const totalTime = Date.now() - startTime;
            return { success: true, data: null as T, status: response.status };
          }
        }

        // Unexpected status code
        const totalTime = Date.now() - startTime;
        return {
          success: false,
          error: `Unexpected response status: ${response.status}`,
          status: response.status
        };

      } catch (networkError) {
        if (retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
          backoffDelay *= 2; // Exponential backoff
          retryCount++;
          continue;
        } else {
          const totalTime = Date.now() - startTime;
          return {
            success: false,
            error: `Network error after max retries: ${networkError}`
          };
        }
      }
    }

    const totalTime = Date.now() - startTime;
    return { success: false, error: 'Max retries exceeded' };
  }, [tokenConfig, dispatch]);

  return { makeApiCall };
} 